import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { isSameDay } from 'date-fns'
import { useParams } from 'react-router-dom'
import { TOPBAR_HEIGHT } from '@/v2/constants'
import PatientRoutes from '@/v2/routes/PatientRoutes'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useTabContext } from '@/v2/contexts/TabsContext'
import { usePatient, usePatientProfile } from '@/v2/hooks/patient/usePatient'
import { usePatientOfficeNotes } from '@/v2/hooks/patient/usePatientNotes'
import { usePatientAppointments } from '@/v2/hooks/patient/usePatientAppointments'
import {
  getDateTimeWithoutTimezone,
  nowOnClinicTimezone,
} from '@/v2/utils/convert'
import PatientBar from './PatientBar'
import ViewLoader from './ViewLoader'
import OverduePaymentWarning from './OverduePaymentWarning'

const PatientWrapper = styled.div`
  display: block;
  max-height: calc(100vh - ${TOPBAR_HEIGHT});
  height: 100%;
  overflow: hidden;
`
const ErrorWrapper = styled.div`
  text-align: center;
  margin-top: 60px;
`

const DesktopPatient = () => {
  const { id } = useParams()
  const { onSetPatient, onSetOfficeNotes } = usePatientContext()
  const { onAddTab } = useTabContext()
  const { patient, isLoading, isError } = usePatient(id)
  const { patient: profileData } = usePatientProfile(id)
  const {
    notes,
    isLoading: isLoadingNotes,
    isError: isErrorNotes,
  } = usePatientOfficeNotes(id)
  const {
    nextAppointments,
    previousAppointments,
    isLoading: isLoadingAppointments,
  } = usePatientAppointments(id)

  const hasInitialized = useRef(false)
  const lastPatientId = useRef(null)

  // Reset refs when patient changes
  if (lastPatientId.current !== id) {
    hasInitialized.current = false
    lastPatientId.current = id
  }

  const isSeated = useMemo(() => {
    if (!nextAppointments && !previousAppointments) return false

    const isToday = date =>
      isSameDay(getDateTimeWithoutTimezone(date), nowOnClinicTimezone())
    const isSeatedToday = appointment =>
      !!appointment?.seated && isToday(appointment?.seatedTime)

    return (
      (nextAppointments?.length > 0 && isSeatedToday(nextAppointments[0])) ||
      (previousAppointments?.length > 0 &&
        isSeatedToday(previousAppointments[0]))
    )
  }, [nextAppointments, previousAppointments])

  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      !isLoadingAppointments &&
      patient &&
      patient.id &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true
      onSetPatient(patient)

      onAddTab({
        id: patient.id,
        name: patient.fullName,
        status: isSeated ? 'SEATED' : undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError, isLoadingAppointments, patient?.id])

  useEffect(() => {
    if (profileData && patient?.id) {
      onSetPatient({
        id: patient.id,
        photo: profileData.photo,
        dob: profileData.dob,
        gender: profileData.gender,
        ssn: profileData.ssn,
        referredBy: profileData.referredBy,
        maAttestedAt: profileData.maAttestedAt,
        address1: profileData.address1,
        address2: profileData.address2,
        city: profileData.city,
        state: profileData.state,
        country: profileData.country,
        zipcode: profileData.zipcode,
        emergencyName: profileData.emergencyName,
        emergencyRelationship: profileData.emergencyRelationship,
        emergencyEmail: profileData.emergencyEmail,
        emergencyPhoneNumber: profileData.emergencyPhoneNumber,
        pregnant: profileData.pregnant,
        premedication: profileData.premedication,
        latexAllergy: profileData.latexAllergy,
        insuranceForms: profileData.insuranceForms || [],
        contract: profileData.contract,
        intakeForm: profileData.intakeForm,
        conversation: profileData.conversation,
        familyMembers: profileData.familyMembers,
        fakeGfPatientId: profileData.fakeGfPatientId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, patient?.id])

  useEffect(() => {
    if (!isLoadingNotes && !isErrorNotes && notes) {
      onSetOfficeNotes(notes)
    }
  }, [isLoadingNotes, isErrorNotes, notes])

  if (isLoading) return <ViewLoader />
  if (isError) {
    // eslint-disable-next-line no-console
    console.error(isError)
    // eslint-disable-next-line react/no-unescaped-entities
    return <ErrorWrapper>Sorry! We couldn't load the page.</ErrorWrapper>
  }

  return (
    <PatientWrapper>
      <PatientBar />
      <OverduePaymentWarning />
      <PatientRoutes />
    </PatientWrapper>
  )
}

export default DesktopPatient
